import axios from 'axios';
import { auth } from '../firebase/config';
import { onAuthStateChanged } from 'firebase/auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance
const apiClient = axios.create({
  baseURL: API
});

// Helper to wait for auth to be ready (handles initial page load)
let authReady = false;
let authReadyPromise = null;

const waitForAuth = () => {
  if (authReady) {
    return Promise.resolve(auth.currentUser);
  }
  
  if (!authReadyPromise) {
    authReadyPromise = new Promise((resolve) => {
      const unsubscribe = onAuthStateChanged(auth, (user) => {
        authReady = true;
        unsubscribe();
        resolve(user);
      });
    });
  }
  
  return authReadyPromise;
};

// Add request interceptor to include Firebase auth token
apiClient.interceptors.request.use(
  async (config) => {
    try {
      // Wait for auth state to be initialized before checking currentUser
      await waitForAuth();
      
      const user = auth.currentUser;
      if (user) {
        const token = await user.getIdToken();
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      console.error('Error getting auth token:', error);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for better error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      console.error('Authentication error:', error.response.data);
      // Optionally redirect to login or show error
    } else if (error.response?.status === 403) {
      console.error('Permission denied:', error.response.data);
    }
    return Promise.reject(error);
  }
);

// Card API
export const cardAPI = {
  // Create a new card
  create: async (cardData) => {
    const response = await apiClient.post('/cards', cardData);
    return response.data;
  },

  // Get all cards
  getAll: async (userId = null) => {
    try {
      const params = userId ? { user_id: userId } : {};
      const response = await apiClient.get('/cards', { params });
      // Ensure we always return an array
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Error fetching cards:', error);
      return []; // Return empty array on error
    }
  },

  // Get card by username
  getByUsername: async (username) => {
    const response = await apiClient.get(`/cards/${username}`);
    return response.data;
  },

  // Update card
  update: async (username, cardData) => {
    const response = await apiClient.put(`/cards/${username}`, cardData);
    return response.data;
  },

  // Delete card
  delete: async (username) => {
    await apiClient.delete(`/cards/${username}`);
  },

  // Search cards
  search: async (query, userId = null) => {
    try {
      const params = userId ? { user_id: userId } : {};
      const response = await apiClient.get(`/cards/search/${encodeURIComponent(query)}`, { params });
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Error searching cards:', error);
      return [];
    }
  },

  // Get authenticated user's cards (using JWT token)
  getMyCards: async () => {
    try {
      const response = await apiClient.get(`/cards/my-cards`);
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Error fetching my cards:', error);
      return [];
    }
  },

  // Get all cards by user ID (admin use or specific user lookup)
  getByUserId: async (userId) => {
    try {
      const response = await apiClient.get(`/cards/user/${userId}`);
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Error fetching user cards:', error);
      return [];
    }
  },

  // Get card by ID
  getById: async (cardId) => {
    const response = await apiClient.get(`/cards/id/${cardId}`);
    return response.data;
  },

  // Check if card name is available for current user
  checkNameAvailability: async (name, excludeUsername = null) => {
    const params = { name };
    if (excludeUsername) {
      params.exclude_username = excludeUsername;
    }
    const response = await apiClient.get('/cards/check-name-availability', { params });
    return response.data;
  },

  // Get top 3 cards for dashboard (optimized with minimal data)
  getDashboardTop: async () => {
    try {
      const response = await apiClient.get('/cards/dashboard/top');
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Error fetching dashboard cards:', error);
      return [];
    }
  },

  // Assign card to another user (Admin only)
  assignToUser: async (cardId, userEmail, adminEmail) => {
    const response = await apiClient.post(`/cards/${cardId}/assign`, {
      userEmail: userEmail
    }, {
      headers: {
        'X-Admin-Email': adminEmail
      }
    });
    return response.data;
  }
};

// Contact API
export const contactAPI = {
  // Create a new contact
  create: async (contactData) => {
    const response = await apiClient.post('/contacts', contactData);
    return response.data;
  },

  // Get all contacts
  getAll: async () => {
    try {
      const response = await apiClient.get('/contacts');
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Error fetching contacts:', error);
      return [];
    }
  },

  // Get contacts by card ID
  getByCardId: async (cardId) => {
    try {
      const response = await apiClient.get(`/contacts/card/${cardId}`);
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Error fetching card contacts:', error);
      return [];
    }
  },

  // Get contacts by user ID
  getByUserId: async (userId) => {
    try {
      const response = await apiClient.get(`/contacts/user/${userId}`);
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Error fetching user contacts:', error);
      return [];
    }
  },

  // Update contact notes
  updateNotes: async (contactId, notes) => {
    const response = await apiClient.patch(`/contacts/${contactId}`, { notes });
    return response.data;
  },

  // Delete contact
  delete: async (contactId) => {
    await apiClient.delete(`/contacts/${contactId}`);
  },

  // Check if contact exists for user and card
  checkExists: async (userId, cardId) => {
    const response = await apiClient.get(`/contacts/check/${userId}/${cardId}`);
    return response.data;
  },

  // Get top 3 contacts for dashboard (optimized with minimal data)
  getDashboardTop: async () => {
    try {
      const response = await apiClient.get('/contacts/dashboard/top');
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Error fetching dashboard contacts:', error);
      return [];
    }
  },

  // Import contacts from CSV/Excel file
  importContacts: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post('/contacts/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  // Get import status
  getImportStatus: async () => {
    try {
      const response = await apiClient.get('/contacts/import/status');
      return response.data;
    } catch (error) {
      console.error('Error fetching import status:', error);
      return { status: 'none' };
    }
  },

  // Migrate imported contacts (fixes ID format for contacts imported before fix)
  migrateImportedContacts: async () => {
    try {
      const response = await apiClient.post('/contacts/import/migrate');
      return response.data;
    } catch (error) {
      console.error('Error migrating contacts:', error);
      return { success: false, error: error.message };
    }
  }
};

// Contact Relationships API (for reciprocal digital card connections)
export const contactRelationshipAPI = {
  // Create a bidirectional relationship
  create: async (connectedUsername) => {
    const response = await apiClient.post('/contact-relationships', {
      connected_username: connectedUsername
    });
    return response.data;
  },

  // Get current user's contacts (returns live card data)
  getMyContacts: async () => {
    try {
      const user = auth.currentUser;
      if (!user) {
        console.error('❌ No authenticated user found for getMyContacts');
        return [];
      }
      console.log('🔍 Fetching digital contacts for user:', user.uid);
      const response = await apiClient.get(`/contact-relationships/user/${user.uid}`);
      console.log('✅ Digital contacts fetched:', response.data?.length || 0);
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('❌ Error fetching my contacts:', error);
      console.error('Error details:', error.response?.data);
      return [];
    }
  },

  // Get all relationships for a user (returns live card data)
  getUserRelationships: async (userId) => {
    try {
      const response = await apiClient.get(`/contact-relationships/user/${userId}`);
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Error fetching relationships:', error);
      return [];
    }
  },

  // Remove a contact (by relationship ID)
  removeContact: async (relationshipId) => {
    await apiClient.delete(`/contact-relationships/${relationshipId}`);
  },

  // Delete a relationship (removes both directions)
  delete: async (relationshipId) => {
    await apiClient.delete(`/contact-relationships/${relationshipId}`);
  },

  // Check if relationship exists
  checkExists: async (username) => {
    const response = await apiClient.get('/contact-relationships/check', {
      params: { username }
    });
    return response.data;
  },

  // Update notes for a relationship
  updateNotes: async (relationshipId, notes) => {
    const response = await apiClient.patch(`/contact-relationships/${relationshipId}/notes`, null, {
      params: { notes }
    });
    return response.data;
  },

  // Get a single relationship by ID
  getById: async (relationshipId) => {
    const response = await apiClient.get(`/contact-relationships/${relationshipId}`);
    return response.data;
  }
};

// Notifications API
export const notificationAPI = {
  // Get notifications
  getAll: async (limit = 50, unreadOnly = false) => {
    try {
      const response = await apiClient.get('/notifications', {
        params: { limit, unread_only: unreadOnly }
      });
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Error fetching notifications:', error);
      return [];
    }
  },

  // Get unread count
  getUnreadCount: async () => {
    try {
      const response = await apiClient.get('/notifications/unread-count');
      return response.data.count || 0;
    } catch (error) {
      console.error('Error fetching unread count:', error);
      return 0;
    }
  },

  // Mark as read
  markAsRead: async (notificationId) => {
    const response = await apiClient.patch(`/notifications/${notificationId}/read`);
    return response.data;
  },

  // Mark all as read
  markAllAsRead: async () => {
    const response = await apiClient.patch('/notifications/mark-all-read');
    return response.data;
  },

  // Delete notification
  delete: async (notificationId) => {
    await apiClient.delete(`/notifications/${notificationId}`);
  }
};


// Team API
export const teamAPI = {
  // Create team
  createTeam: async (teamName) => {
    const response = await apiClient.post('/teams', { team_name: teamName });
    return response.data;
  },

  // Get my team
  getMyTeam: async () => {
    const response = await apiClient.get('/teams/my-team');
    return response.data;
  },

  // Invite member
  inviteMember: async (teamId, memberData) => {
    const response = await apiClient.post(`/teams/${teamId}/invite`, memberData);
    return response.data;
  },

  // Get pending invitations
  getPendingInvitations: async () => {
    const response = await apiClient.get('/teams/invitations/pending');
    return response.data;
  },

  // Accept invitation
  acceptInvitation: async (invitationId) => {
    const response = await apiClient.post(`/teams/invitations/${invitationId}/accept`);
    return response.data;
  },

  // Reject invitation
  rejectInvitation: async (invitationId) => {
    const response = await apiClient.post(`/teams/invitations/${invitationId}/reject`);
    return response.data;
  },

  // Remove member
  removeMember: async (teamId, memberUserId) => {
    const response = await apiClient.delete(`/teams/${teamId}/members/${memberUserId}`);
    return response.data;
  },

  // Update member
  updateMember: async (teamId, memberUserId, designation) => {
    const response = await apiClient.patch(`/teams/${teamId}/members/${memberUserId}`, null, {
      params: { designation }
    });
    return response.data;
  },

  // Assign lead
  assignLead: async (leadId, assignedTo, assignmentNote) => {
    const response = await apiClient.post(`/crm/leads/${leadId}/assign`, {
      assigned_to: assignedTo,
      assignment_note: assignmentNote
    });
    return response.data;
  },

  // Get team leads
  getTeamLeads: async () => {
    const response = await apiClient.get('/crm/team/leads');
    return response.data;
  },

  // Get invitation link
  getInvitationLink: async (teamId, invitationToken) => {
    const response = await apiClient.get(`/teams/${teamId}/invitations/${invitationToken}/link`);
    return response.data;
  },

  // Resend invitation
  resendInvitation: async (teamId, invitationToken) => {
    const response = await apiClient.post(`/teams/${teamId}/invitations/${invitationToken}/resend`);
    return response.data;
  },

  // Delete invitation
  deleteInvitation: async (teamId, invitationToken) => {
    const response = await apiClient.delete(`/teams/${teamId}/invitations/${invitationToken}`);
    return response.data;
  },

  // Delete team
  deleteTeam: async (teamId) => {
    const response = await apiClient.delete(`/teams/${teamId}`);
    return response.data;
  },

  // Update member settings
  updateMemberSettings: async (teamId, memberUserId, dealVisibility, amountVisibility, cardEditPermission, canManagePipelines = false, copyContactsToOwner = false) => {
    const response = await apiClient.patch(`/teams/${teamId}/members/${memberUserId}/settings`, null, {
      params: {
        deal_visibility: dealVisibility,
        amount_visibility: amountVisibility,
        card_edit_permission: cardEditPermission,
        can_manage_pipelines: canManagePipelines,
        copy_contacts_to_owner: copyContactsToOwner
      }
    });
    return response.data;
  },

  // Update member pipeline access
  updateMemberPipelineAccess: async (teamId, memberUserId, pipelineIds) => {
    const response = await apiClient.patch(`/teams/${teamId}/members/${memberUserId}/pipelines`, pipelineIds, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  },

  // Update team profile
  updateTeamProfile: async (teamId, profileData) => {
    const response = await apiClient.put(`/teams/${teamId}/profile`, profileData);
    return response.data;
  },

  // Get team member's contacts
};

// Scanned Contact API
export const scannedContactAPI = {
  // Process and save card (creates placeholder, processes in background)
  processAndSave: async (formData) => {
    const response = await apiClient.post('/scanned-contacts/process-and-save', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  // Process OCR on image (legacy)
  processOCR: async (formData) => {
    const response = await apiClient.post('/scanned-contacts/ocr/process', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  // Create scanned contact
  create: async (formData) => {
    const response = await apiClient.post('/scanned-contacts/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  // Get all scanned contacts for current user (with pagination, search, and creator filter)
  getAll: async (page = 1, limit = 10, search = '', creators = undefined) => {
    try {
      const params = { page, limit };
      if (search && search.trim()) {
        params.search = search.trim();
      }
      if (creators && creators.trim()) {
        params.created_by = creators.trim();
      }
      const response = await apiClient.get('/scanned-contacts/', { params });
      return response.data; // Returns { data, total, page, limit, totalPages }
    } catch (error) {
      console.error('Error fetching scanned contacts:', error);
      return { data: [], total: 0, page: 1, limit: 10, totalPages: 0 };
    }
  },

  // Get specific scanned contact
  getById: async (contactId) => {
    const response = await apiClient.get(`/scanned-contacts/${contactId}`);
    return response.data;
  },

  // Update scanned contact
  update: async (contactId, contactData) => {
    const response = await apiClient.put(`/scanned-contacts/${contactId}`, contactData);
    return response.data;
  },

  // Delete scanned contact
  delete: async (contactId) => {
    await apiClient.delete(`/scanned-contacts/${contactId}`);
  },

  // Search across all contacts
  search: async (query) => {
    const response = await apiClient.get(`/contacts/search?q=${encodeURIComponent(query)}`);
    return response.data;
  },

  // Get all scanned contacts for review with pagination and search (Admin/Reviewer only)
  getAllForReview: async (page = 1, limit = 10, search = '') => {
    try {
      const params = { page, limit };
      if (search && search.trim()) {
        params.search = search.trim();
      }
      const response = await apiClient.get('/scanned-contacts/review/all', { params });
      return response.data; // Returns { data, total, page, limit, totalPages }
    } catch (error) {
      console.error('Error fetching contacts for review:', error);
      throw error;
    }
  },

  // Mark contact as reviewed
  markAsReviewed: async (contactId) => {
    const response = await apiClient.patch(`/scanned-contacts/${contactId}/review`);
    return response.data;
  },

  // Mark contact as unreviewed
  markAsUnreviewed: async (contactId) => {
    const response = await apiClient.patch(`/scanned-contacts/${contactId}/unreview`);
    return response.data;
  },

  // Delete contact (for review - Admin/Reviewer only, bypasses ownership check)
  deleteForReview: async (contactId) => {
    await apiClient.delete(`/scanned-contacts/review/${contactId}`);
  },

  // Assign contact to another user (Admin only)
  assignToUser: async (contactId, userEmail) => {
    const response = await apiClient.post(`/scanned-contacts/${contactId}/assign`, {
      userEmail: userEmail
    });
    return response.data;
  }
};

// Analytics API
export const analyticsAPI = {
  // Track card view
  trackView: async (cardId, cardUsername, viewerInfo = {}) => {
    try {
      await apiClient.post('/analytics/card-view', {
        cardId,
        cardUsername,
        viewerInfo
      });
    } catch (error) {
      console.error('Error tracking view:', error);
    }
  },

  // Track link click
  trackLinkClick: async (cardId, cardUsername, linkType, linkValue, viewerInfo = {}) => {
    try {
      await apiClient.post('/analytics/link-click', {
        cardId,
        cardUsername,
        linkType,
        linkValue,
        viewerInfo
      });
    } catch (error) {
      console.error('Error tracking link click:', error);
    }
  },

  // Get user analytics
  getUserAnalytics: async (userId) => {
    const response = await apiClient.get(`/analytics/user/${userId}/analytics`);
    return response.data;
  },

  // Get card views for multiple cards (batch) - Optimized for My Cards page
  getBatchCardViews: async (cardIds) => {
    try {
      const response = await apiClient.post(`/analytics/cards/batch-views`, cardIds);
      return response.data;
    } catch (error) {
      console.error('Error fetching batch card views:', error);
      return {};
    }
  },

  // Get card views
  getCardViews: async (cardId) => {
    try {
      const response = await apiClient.get(`/analytics/card/${cardId}/views`);
      return response.data;
    } catch (error) {
      console.error('Error fetching card views:', error);
      return { views: [], totalViews: 0, uniqueViewers: 0 };
    }
  },

  // Get card link clicks
  getCardLinkClicks: async (cardId) => {
    try {
      const response = await apiClient.get(`/analytics/card/${cardId}/link-clicks`);
      return response.data;
    } catch (error) {
      console.error('Error fetching card link clicks:', error);
      return { clicks: [], totalClicks: 0, linkStats: [] };
    }
  },

  // Get card contact saves
  getCardSaves: async (cardId) => {
    try {
      const response = await apiClient.get(`/analytics/card/${cardId}/saves`);
      return response.data;
    } catch (error) {
      console.error('Error fetching card saves:', error);
      return { total_saves: 0, recent_saves: [] };
    }
  },

  // Get user link clicks
  getUserLinkClicks: async (userId) => {
    try {
      const response = await apiClient.get(`/analytics/user/${userId}/link-clicks`);
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Error fetching user link clicks:', error);
      return [];
    }
  }
};

// CRM API
export const crmAPI = {
  // Get lead statistics (for free user limits)
  getLeadStats: async () => {
    try {
      const response = await apiClient.get('/crm/leads/stats');
      return response.data;
    } catch (error) {
      console.error('Error fetching lead stats:', error);
      return null;
    }
  },

  // Get all leads
  getLeads: async () => {
    try {
      const response = await apiClient.get('/crm/leads');
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Error fetching CRM leads:', error);
      return [];
    }
  },

  // Create a new lead
  createLead: async (leadData) => {
    const response = await apiClient.post('/crm/leads', leadData);
    return response.data.lead; // Return the lead object, not the wrapper
  },

  // Get a specific lead
  getLead: async (leadId) => {
    const response = await apiClient.get(`/crm/leads/${leadId}`);
    return response.data;
  },

  // Update a lead
  updateLead: async (leadId, updateData) => {
    const response = await apiClient.put(`/crm/leads/${leadId}`, updateData);
    return response.data;
  },

  // Delete a lead
  deleteLead: async (leadId) => {
    await apiClient.delete(`/crm/leads/${leadId}`);
  },

  // Add note to a lead
  addNote: async (leadId, noteText) => {
    const response = await apiClient.post(`/crm/leads/${leadId}/notes`, { text: noteText });
    return response.data;
  },

  // Update lead stage (for drag-and-drop)
  updateLeadStage: async (leadId, stageId, position) => {
    const response = await apiClient.put(`/crm/leads/${leadId}/stage`, null, {
      params: { stageId, position }
    });
    return response.data;
  },

  // Get all stages
  getStages: async () => {
    try {
      const response = await apiClient.get('/crm/stages');
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Error fetching CRM stages:', error);
      return [];
    }
  },

  // Create a new stage
  createStage: async (stageData) => {
    const response = await apiClient.post('/crm/stages', stageData);
    return response.data;
  },

  // Update a stage
  updateStage: async (stageId, updateData) => {
    const response = await apiClient.put(`/crm/stages/${stageId}`, updateData);
    return response.data;
  },

  // Delete a stage
  deleteStage: async (stageId) => {
    await apiClient.delete(`/crm/stages/${stageId}`);
  },

  // Reorder stages
  reorderStages: async (stagesOrder) => {
    const response = await apiClient.put('/crm/stages/reorder', { stages: stagesOrder });
    return response.data;
  },

  // Get all templates
  getTemplates: async () => {
    try {
      const response = await apiClient.get('/crm/templates');
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Error fetching CRM templates:', error);
      return [];
    }
  },

  // Create a new template
  createTemplate: async (templateData) => {
    const response = await apiClient.post('/crm/templates', templateData);
    return response.data;
  },

  // Delete a template
  deleteTemplate: async (templateId) => {
    await apiClient.delete(`/crm/templates/${templateId}`);
  },

  // Pipeline Management
  // Get all pipeline templates
  getPipelineTemplates: async () => {
    try {
      const response = await apiClient.get('/crm/pipelines/templates');
      return response.data.templates || [];
    } catch (error) {
      console.error('Error fetching pipeline templates:', error);
      return [];
    }
  },

  // Get user's pipelines
  getPipelines: async () => {
    try {
      const response = await apiClient.get('/crm/pipelines');
      return response.data.pipelines || [];
    } catch (error) {
      console.error('Error fetching pipelines:', error);
      return [];
    }
  },

  // Create new pipeline from template
  createPipeline: async (template_key, custom_name = null) => {
    const response = await apiClient.post('/crm/pipelines', {
      template_key,
      custom_name
    });
    return response.data;
  },

  // Activate/Switch pipeline
  activatePipeline: async (pipelineId) => {
    const response = await apiClient.put(`/crm/pipelines/${pipelineId}/activate`);
    return response.data;
  },

  // Delete pipeline
  deletePipeline: async (pipelineId) => {
    const response = await apiClient.delete(`/crm/pipelines/${pipelineId}`);
    return response.data;
  },

  // Import deals from CSV/Excel file
  importDeals: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post('/crm/leads/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  // Get import status
  getImportStatus: async () => {
    try {
      const response = await apiClient.get('/crm/leads/import/status');
      return response.data;
    } catch (error) {
      console.error('Error fetching import status:', error);
      return { status: 'none' };
    }
  },

  // Upload document for a deal
  uploadDocument: async (leadId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(`/crm/leads/${leadId}/documents`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  // Get documents for a deal
  getDocuments: async (leadId) => {
    const response = await apiClient.get(`/crm/leads/${leadId}/documents`);
    return response.data;
  },

  // Download document
  downloadDocument: async (leadId, docId, filename) => {
    const response = await apiClient.get(`/crm/leads/${leadId}/documents/${docId}/download`, {
      responseType: 'blob'
    });
    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  // Delete document
  deleteDocument: async (leadId, docId) => {
    const response = await apiClient.delete(`/crm/leads/${leadId}/documents/${docId}`);
    return response.data;
  },

  // ============== Custom Fields ==============
  
  // Get custom fields for a pipeline
  getCustomFields: async (pipelineId) => {
    try {
      const response = await apiClient.get(`/crm/pipelines/${pipelineId}/custom-fields`);
      return response.data.custom_fields || [];
    } catch (error) {
      console.error('Error fetching custom fields:', error);
      return [];
    }
  },

  // Create custom field
  createCustomField: async (pipelineId, fieldData) => {
    const response = await apiClient.post(`/crm/pipelines/${pipelineId}/custom-fields`, fieldData);
    return response.data;
  },

  // Update custom field
  updateCustomField: async (pipelineId, fieldId, fieldData) => {
    const response = await apiClient.put(`/crm/pipelines/${pipelineId}/custom-fields/${fieldId}`, fieldData);
    return response.data;
  },

  // Delete custom field
  deleteCustomField: async (pipelineId, fieldId) => {
    const response = await apiClient.delete(`/crm/pipelines/${pipelineId}/custom-fields/${fieldId}`);
    return response.data;
  },

  // ============== Reminder Management ==============
  
  // Remove reminder from a lead/deal
  removeReminder: async (leadId) => {
    const response = await apiClient.delete(`/crm/leads/${leadId}/reminder`);
    return response.data;
  },

  // ============== Filter Leads ==============
  
  // Filter leads with advanced conditions
  filterLeads: async (filterData) => {
    const response = await apiClient.post('/crm/leads/filter', filterData);
    return response;
  },

  // Get saved filters for a pipeline
  getSavedFilters: async (pipelineId) => {
    try {
      const response = await apiClient.get(`/crm/pipelines/${pipelineId}/saved-filters`);
      return response.data.filters || [];
    } catch (error) {
      console.error('Error fetching saved filters:', error);
      return [];
    }
  },

  // Save a filter
  saveFilter: async (pipelineId, filterData) => {
    const response = await apiClient.post(`/crm/pipelines/${pipelineId}/saved-filters`, filterData);
    return response.data;
  },

  // Submit demo request (public - no auth required)
  submitDemoRequest: async (cardUsername, demoData) => {
    const response = await axios.post(`${API}/crm/leads/demo-request/${cardUsername}`, demoData);
    return response.data;
  }
};


// Coupon API
export const couponAPI = {
  // Get available coupons (public)
  getAvailableCoupons: async () => {
    const response = await axios.get(`${API}/coupons/available`);
    return response.data;
  },

  // Validate coupon for specific tier
  validateCoupon: async (code, tier) => {
    const response = await axios.get(`${API}/coupons/validate/${code}/${tier}`);
    return response.data;
  }
};

// Subscription API
export const subscriptionAPI = {
  // Get user's subscription status
  getStatus: async () => {
    const user = auth.currentUser;
    if (!user) {
      return {
        tier: 'free',
        status: 'inactive',
        hasAccess: false,
        cardLimit: 1
      };
    }
    
    const token = await user.getIdToken();
    const response = await axios.get(`${API}/payments/subscription-status`, {
      headers: {
        'X-User-Id': user.uid,
        'Authorization': `Bearer ${token}`
      }
    });
    return response.data;
  }
};

// Country Pricing API
export const countryPricingAPI = {
  // Auto-detect country from IP
  detectCountry: async () => {
    const response = await axios.get(`${API}/pricing/detect`);
    return response.data;
  },

  // Get pricing for specific country
  getCountryPricing: async (countryCode) => {
    const response = await axios.get(`${API}/pricing/country/${countryCode}`);
    return response.data;
  },

  // Get all countries with pricing (public)
  getAllCountries: async () => {
    const response = await axios.get(`${API}/pricing/countries`);
    return response.data;
  },

  // Admin: Get all countries (including inactive)
  adminGetAllCountries: async () => {
    const response = await apiClient.get('/pricing/admin/countries');
    return response.data;
  },

  // Admin: Create or update country pricing
  adminCreateCountry: async (countryData) => {
    const response = await apiClient.post('/pricing/admin/country', countryData);
    return response.data;
  },

  // Admin: Update country pricing
  adminUpdateCountry: async (countryCode, updates) => {
    const response = await apiClient.put(`/pricing/admin/country/${countryCode}`, updates);
    return response.data;
  },

  // Admin: Delete country pricing
  adminDeleteCountry: async (countryCode) => {
    const response = await apiClient.delete(`/pricing/admin/country/${countryCode}`);
    return response.data;
  }
};

// Admin API
export const adminAPI = {
  // Check if current user is admin
  checkAdminStatus: async () => {
    const response = await apiClient.get('/admin/check-admin-status');
    return response.data;
  }
};

// Orders API
export const ordersAPI = {
  // Get user's orders
  getMyOrders: async () => {
    const response = await apiClient.get('/shop/orders');
    return response.data;
  }
};

